import React from 'react';
import { BarChart2, Calculator, Building } from 'lucide-react';
import Button from '../../../../../components/ui/Button';
import { formatCurrency } from '../../../../../utils/formatting';

/**
 * Компонент боковой панели для быстрой оценки стоимости архитектурного проекта
 */
const QuickEstimationSidebar = ({ 
  formData,
  setFormData,
  errors,
  setErrors,
  isCalculating,
  projectTypes,
  complexityLevels,
  projectStages
}) => {
  // Расчет текущей площади объекта
  const buildingArea = parseFloat(formData.buildingArea) || 0;
  
  // Расчет текущего количества этажей
  const numFloors = parseInt(formData.numFloors) || 0;
  
  // Обработчик изменения площади объекта
  const handleBuildingAreaChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      buildingArea: newValue
    }));
    
    // Очищаем ошибку если она есть
    if (errors.buildingArea) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.buildingArea;
        return newErrors;
      });
    }
  };
  
  // Обработчик изменения количества этажей
  const handleNumFloorsChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      numFloors: newValue
    }));
    
    // Очищаем ошибку если она есть
    if (errors.numFloors) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.numFloors;
        return newErrors;
      });
    }
  };
  
  // Обработчик изменения сложности проекта
  const handleComplexityChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      complexity: newValue
    }));
    
    // Очищаем ошибку если она есть
    if (errors.complexity) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.complexity;
        return newErrors;
      });
    }
  };
  
  // Предопределенные значения площади для быстрого выбора
  const predefinedAreas = [
    { value: '50', label: '50 м²' },
    { value: '100', label: '100 м²' },
    { value: '250', label: '250 м²' },
    { value: '500', label: '500 м²' }
  ];
  
  // Функция расчета приблизительной стоимости
  const calculateQuickEstimate = () => {
    // Быстрый расчет стоимости на основе текущих параметров формы
    const selectedProjectType = projectTypes.find(t => t.id === formData.projectType) || projectTypes[0];
    const baseRatePerSqm = selectedProjectType ? selectedProjectType.defaultRatePerSqm : 1000;
    
    const selectedComplexity = complexityLevels.find(c => c.id === formData.complexity) || complexityLevels[1];
    const complexityFactor = selectedComplexity ? selectedComplexity.factor : 1.0;
    
    const selectedStage = projectStages.find(s => s.id === formData.projectStage) || projectStages[0];
    const stagePercentage = selectedStage ? selectedStage.percentage / 100 : 0.2;
    
    const floorsFactor = 1 + (numFloors > 1 ? (numFloors - 1) * 0.05 : 0);
    
    const estimatedCost = buildingArea * baseRatePerSqm * complexityFactor * stagePercentage * floorsFactor;
    
    return {
      baseCost: estimatedCost,
      totalCost: estimatedCost * (formData.includeInteriorDesign ? 1.4 : 1) * 
                             (formData.includeVisualization ? 1.1 : 1) *
                             (formData.includeProjectSupervision ? 1.15 : 1)
    };
  };
  
  // Получаем приблизительную оценку
  const estimatedCosts = buildingArea > 0 ? calculateQuickEstimate() : { baseCost: 0, totalCost: 0 };
  
  // Получение символа валюты
  const getCurrencySymbol = (currency) => {
    switch (currency) {
      case 'USD': return '$';
      case 'EUR': return '€';
      default: return '₽';
    }
  };

  const currencySymbol = getCurrencySymbol(formData.currencyType);
  
  // Функция форматирования валюты с нужным символом
  const formatCurrencyWithSymbol = (amount) => {
    return `${amount.toLocaleString('ru-RU')} ${currencySymbol}`;
  };
  
  // Функция расчета
  const calculateFull = () => {
    // Здесь должна быть вызвана функция основного расчета
    // В данном случае просто эмулируем действие, так как основная логика 
    // находится в родительском компоненте
    document.querySelector('button[type="submit"]')?.click();
  };
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Calculator className="w-5 h-5 mr-2 text-indigo-500" />
        Быстрая оценка стоимости
      </h3>
      
      <div className="space-y-5">
        {/* Тип проекта */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="projectTypeSelect" className="text-gray-600 dark:text-gray-400">Тип проекта</label>
          </div>
          <select
            id="projectTypeSelect"
            name="projectType"
            value={formData.projectType}
            onChange={(e) => {
              setFormData(prev => ({ ...prev, projectType: e.target.value }))
            }}
            className="form-select w-full"
          >
            {projectTypes.map(type => (
              <option key={type.id} value={type.id}>
                {type.name}
              </option>
            ))}
          </select>
        </div>
        
        {/* Площадь здания */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="buildingAreaSlider" className="text-gray-600 dark:text-gray-400">Площадь объекта</label>
            <span className="font-medium text-gray-900 dark:text-white">{buildingArea} м²</span>
          </div>
          <input
            type="range"
            id="buildingAreaSlider"
            min="30"
            max="1000"
            step="10"
            value={buildingArea}
            onChange={handleBuildingAreaChange}
            aria-label="Площадь объекта"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-indigo-600"
          />
          
          {/* Кнопки с предопределенными площадями */}
          <div className="grid grid-cols-4 gap-1 mt-2">
            {predefinedAreas.map(area => (
              <button
                key={area.value}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, buildingArea: area.value }))}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${formData.buildingArea === area.value 
                    ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {area.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Количество этажей */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="numFloorsSlider" className="text-gray-600 dark:text-gray-400">Количество этажей</label>
            <span className="font-medium text-gray-900 dark:text-white">
              {numFloors} {numFloors === 1 ? 'этаж' : numFloors < 5 ? 'этажа' : 'этажей'}
            </span>
          </div>
          <input
            type="range"
            id="numFloorsSlider"
            min="1"
            max="10"
            step="1"
            value={numFloors}
            onChange={handleNumFloorsChange}
            aria-label="Количество этажей"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-indigo-600"
          />
          
          {/* Кнопки с предопределенными этажностями */}
          <div className="grid grid-cols-4 gap-1 mt-2">
            {[1, 2, 3, 5].map(floors => (
              <button
                key={floors}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, numFloors: floors.toString() }))}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${parseInt(formData.numFloors) === floors 
                    ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {floors} {floors === 1 ? 'этаж' : floors < 5 ? 'этажа' : 'этажей'}
              </button>
            ))}
          </div>
        </div>
        
        {/* Сложность проекта */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="complexitySelect" className="text-gray-600 dark:text-gray-400">Сложность проекта</label>
          </div>
          <select
            id="complexitySelect"
            name="complexity"
            value={formData.complexity}
            onChange={handleComplexityChange}
            className="form-select w-full"
          >
            {complexityLevels.map(level => (
              <option key={level.id} value={level.id}>
                {level.name} {level.factor !== 1 ? `(коэф. ${level.factor})` : ''}
              </option>
            ))}
          </select>
        </div>
        
        {/* Предварительная оценка */}
        <div className="mt-4 bg-indigo-50 dark:bg-indigo-900/20 p-4 rounded-lg border border-indigo-100 dark:border-indigo-800">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium text-indigo-700 dark:text-indigo-300">Базовая стоимость:</span>
            <span className="font-bold text-indigo-700 dark:text-indigo-300">
              {formatCurrencyWithSymbol(estimatedCosts.baseCost)}
            </span>
          </div>
          <div className="flex justify-between items-center mb-3">
            <span className="text-sm font-medium text-indigo-700 dark:text-indigo-300">С доп. услугами:</span>
            <span className="font-bold text-indigo-700 dark:text-indigo-300">
              {formatCurrencyWithSymbol(estimatedCosts.totalCost)}
            </span>
          </div>
          <div className="text-xs text-indigo-600 dark:text-indigo-400">
            * Предварительная оценка. Для точного расчета используйте основную форму.
          </div>
        </div>
        
        {/* Кнопка точного расчета */}
        <Button
          primary
          fullWidth
          onClick={calculateFull}
          icon={<BarChart2 className="w-4 h-4" />}
          disabled={isCalculating || Object.keys(errors).length > 0}
          isLoading={isCalculating}
        >
          Рассчитать точно
        </Button>
      </div>
    </div>
  );
};

export default QuickEstimationSidebar;